import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  fetchListing,
  updateListingStatus,
  fetchListingHistory,
  type Listing,
  type ListingStatusHistory,
} from '../../features/marketplace/api';
import { Button } from '../../components/ui/Button';

export function MarketplaceAdminDetailPage() {
  const navigate = useNavigate();
  const { listingId } = useParams<{ listingId: string }>();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [adminComment, setAdminComment] = useState('');
  const [showCommentForm, setShowCommentForm] = useState<'approve' | 'reject' | null>(null);
  const [history, setHistory] = useState<ListingStatusHistory[]>([]);

  useEffect(() => {
    if (listingId) {
      loadListing();
      loadHistory();
    }
  }, [listingId]);

  async function loadListing() {
    if (!listingId) return;

    try {
      setLoading(true);
      const data = await fetchListing(listingId);
      setListing(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listing');
      setListing(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    if (!listingId) return;

    try {
      const data = await fetchListingHistory(listingId);
      setHistory(data || []);
    } catch (err) {
      console.error('Failed to load history:', err);
      setHistory([]);
    }
  }

  async function handleApproveWithComment() {
    if (!listingId || !adminComment.trim()) {
      setError('Please enter a comment');
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      await updateListingStatus(listingId, 'approved', adminComment.trim());
      await loadHistory(); // Reload history to show new entry
      await loadListing(); // Reload listing to show updated status
      setShowCommentForm(null);
      setAdminComment('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve listing');
    } finally {
      setUpdating(false);
    }
  }

  async function handleRejectWithComment() {
    if (!listingId || !adminComment.trim()) {
      setError('Please enter a comment');
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      await updateListingStatus(listingId, 'rejected', adminComment.trim());
      await loadHistory(); // Reload history to show new entry
      await loadListing(); // Reload listing to show updated status
      setShowCommentForm(null);
      setAdminComment('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject listing');
    } finally {
      setUpdating(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <div className="text-center text-slate-600 dark:text-slate-400 py-12">Loading listing...</div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 mb-6">
            Listing not found
          </div>
          <Button onClick={() => navigate('/marketplace/admin')}>Back to Admin Panel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Review Listing</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">Review all details before approving or rejecting</p>
          </div>
          <Button onClick={() => navigate('/marketplace/admin')} variant="secondary">
            ← Back to Admin Panel
          </Button>
        </div>

        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 mb-6">
            {error}
          </div>
        )}

        {/* Comment Form */}
        {showCommentForm && (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {showCommentForm === 'approve' ? 'Approve Listing' : 'Reject Listing'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Add a comment to let the seller know your decision:
            </p>
            <textarea
              value={adminComment}
              onChange={(e) => setAdminComment(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 mb-4 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              rows={4}
              placeholder={
                showCommentForm === 'approve'
                  ? 'e.g., Great listing! All requirements met.'
                  : 'e.g., Please add more detailed description and clearer photos.'
              }
              required
            />
            <div className="flex gap-2">
              <Button
                onClick={showCommentForm === 'approve' ? handleApproveWithComment : handleRejectWithComment}
                disabled={updating || !adminComment.trim()}
                className={showCommentForm === 'approve' ? 'bg-green-700 hover:bg-green-600' : 'bg-red-700 hover:bg-red-600'}
              >
                {updating ? 'Processing...' : `Confirm ${showCommentForm === 'approve' ? 'Approval' : 'Rejection'}`}
              </Button>
              <Button
                onClick={() => {
                  setShowCommentForm(null);
                  setAdminComment('');
                  setError(null);
                }}
                variant="secondary"
                disabled={updating}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Status Badge */}
        <div className="mb-6">
          <span
            className={`inline-block px-4 py-2 rounded-lg text-sm font-semibold ${
              listing.status === 'approved'
                ? 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400'
                : listing.status === 'rejected'
                  ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400'
                  : 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400'
            }`}
          >
            Status: {listing.status === 'pending_approval' ? 'Pending Approval' : listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
          </span>
        </div>

        {/* Main Content */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 mb-6">
          {/* Images */}
          {listing.media && listing.media.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">Images ({listing.media.length})</h3>

              {/* Main Image */}
              <div className="bg-slate-100 dark:bg-slate-900 rounded-lg overflow-hidden mb-3" style={{ height: '400px' }}>
                <img
                  src={listing.media[selectedImage]}
                  alt={`${listing.title} - Image ${selectedImage + 1}`}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
                  }}
                />
              </div>

              {/* Thumbnail Strip */}
              {listing.media.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {listing.media.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden border-2 transition-all ${
                        selectedImage === idx ? 'border-brand-500' : 'border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600'
                      }`}
                    >
                      <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Title</label>
              <div className="text-xl font-semibold text-slate-900 dark:text-slate-100">{listing.title}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Seller</label>
              <div className="text-lg text-slate-800 dark:text-slate-200">{listing.sellerAlias}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Description</label>
              <div className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{listing.description}</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Price</label>
                <div className="text-2xl font-bold text-brand-600 dark:text-brand-400">
                  {listing.currency} {(listing.priceCents / 100).toFixed(2)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Currency</label>
                <div className="text-lg text-slate-800 dark:text-slate-200">{listing.currency}</div>
              </div>
            </div>

            {/* Service Duration */}
            {listing.durationMinutes && (
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Service Duration</label>
                <div className="inline-block px-3 py-1 bg-violet-100 dark:bg-violet-900/30 border border-violet-300 dark:border-violet-800 text-violet-700 dark:text-violet-400 rounded text-sm font-medium">
                  {listing.durationMinutes < 60
                    ? `${listing.durationMinutes} minutes`
                    : `${(listing.durationMinutes / 60).toFixed(1)} hour${listing.durationMinutes !== 60 ? 's' : ''}`}
                </div>
              </div>
            )}

            {/* Working Schedule */}
            {(listing.workingDays && listing.workingDays.length > 0) || listing.workStartTime || listing.workEndTime ? (
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Working Schedule</label>
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
                  {listing.workingDays && listing.workingDays.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-slate-600 dark:text-slate-500 mb-2">Working Days</div>
                      <div className="flex flex-wrap gap-2">
                        {listing.workingDays.map(day => (
                          <span
                            key={day}
                            className="px-2 py-1 bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 text-brand-700 dark:text-brand-400 rounded text-sm font-medium capitalize"
                          >
                            {day}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(listing.workStartTime || listing.workEndTime) && (
                    <div>
                      <div className="text-xs font-medium text-slate-600 dark:text-slate-500 mb-2">Working Hours</div>
                      <div className="text-slate-700 dark:text-slate-300">
                        {listing.workStartTime || '09:00'} - {listing.workEndTime || '18:00'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Discussion Thread */}
            {listing.discussionThreadId && (
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">💬 Discussion Thread</label>
                <a
                  href={`/forum/thread/${listing.discussionThreadId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 text-brand-600 dark:text-brand-400 rounded hover:bg-brand-100 dark:hover:bg-brand-900/50 transition-colors"
                >
                  <span>{listing.discussionThreadTitle || 'Discussion Thread'}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <p className="text-xs text-slate-600 dark:text-slate-500 mt-1">
                  Thread ID: {listing.discussionThreadId}
                </p>
              </div>
            )}

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Created</label>
                <div className="text-sm text-slate-700 dark:text-slate-300">
                  {new Date(listing.createdAt).toLocaleDateString()} {new Date(listing.createdAt).toLocaleTimeString()}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Last Updated</label>
                <div className="text-sm text-slate-700 dark:text-slate-300">
                  {new Date(listing.updatedAt).toLocaleDateString()} {new Date(listing.updatedAt).toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status History */}
        {history && history.length > 0 && (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Status History</h3>
            <div className="space-y-3">
              {history.map((item) => (
                <div key={item.id} className="border-l-4 border-brand-600 pl-4 py-2 bg-slate-50 dark:bg-slate-900 rounded-r">
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`font-semibold ${
                        item.status === 'approved'
                          ? 'text-green-700 dark:text-green-400'
                          : item.status === 'rejected'
                            ? 'text-red-700 dark:text-red-400'
                            : 'text-yellow-700 dark:text-yellow-400'
                      }`}
                    >
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1).replace('_', ' ')}
                    </span>
                    <span className="text-xs text-slate-600 dark:text-slate-500">
                      {new Date(item.createdAt).toLocaleDateString()} {new Date(item.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                  {item.adminComment && (
                    <p className="text-sm text-slate-700 dark:text-slate-300 italic">
                      "{item.adminComment}"
                    </p>
                  )}
                  {item.adminUserId && (
                    <p className="text-xs text-slate-600 dark:text-slate-500 mt-1">
                      Admin: {item.adminUserId}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {listing.status === 'pending_approval' && !showCommentForm && (
            <>
              <Button
                onClick={() => setShowCommentForm('approve')}
                disabled={updating}
                className="bg-green-700 hover:bg-green-600"
              >
                ✓ Approve Listing
              </Button>
              <Button
                onClick={() => setShowCommentForm('reject')}
                disabled={updating}
                className="bg-red-700 hover:bg-red-600"
              >
                ✕ Reject Listing
              </Button>
            </>
          )}

          {listing.status === 'rejected' && !showCommentForm && (
            <Button
              onClick={() => setShowCommentForm('approve')}
              disabled={updating}
              className="bg-green-700 hover:bg-green-600"
            >
              Re-approve Listing
            </Button>
          )}

          {listing.status === 'approved' && !showCommentForm && (
            <Button
              onClick={() => setShowCommentForm('reject')}
              disabled={updating}
              className="bg-red-700 hover:bg-red-600"
            >
              Revoke Approval
            </Button>
          )}

          <Button
            onClick={() => navigate('/marketplace/admin')}
            variant="secondary"
          >
            Back to Admin Panel
          </Button>
        </div>
      </div>
    </div>
  );
}
